import { useEffect, useRef, useState } from "react";
import { Slider } from "../ui/slider";

const VIEW = 240;

// Circular crop/reposition preview. Reports the crop square in the image's own pixels.
export function PhotoCropper({ file, onCrop }) {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const el = new Image();
    el.onload = () => {
      const s = VIEW / Math.min(el.naturalWidth, el.naturalHeight);
      setImg({ url, w: el.naturalWidth, h: el.naturalHeight, base: s });
      setZoom(1);
      setPos({ x: (VIEW - el.naturalWidth * s) / 2, y: (VIEW - el.naturalHeight * s) / 2 });
    };
    el.onerror = () => setImg({ url, error: true });
    el.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const scale = img && !img.error ? img.base * zoom : 1;
  const clamp = (p, s = scale) => ({
    x: Math.min(0, Math.max(VIEW - img.w * s, p.x)),
    y: Math.min(0, Math.max(VIEW - img.h * s, p.y)),
  });

  useEffect(() => {
    if (!img || img.error) { onCrop(null); return; }
    onCrop({ x: -pos.x / scale, y: -pos.y / scale, size: VIEW / scale });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, pos, scale]);

  if (!img) return <div className="h-[240px]" data-testid="photo-cropper-loading" />;
  if (img.error) return <p className="text-sm text-red-700" data-testid="photo-cropper-error">That file can't be previewed. Choose a JPEG, PNG or WebP photo.</p>;

  const setZoomKeepCenter = (z) => {
    const ns = img.base * z, c = VIEW / 2;
    setPos(clamp({ x: c - ((c - pos.x) / scale) * ns, y: c - ((c - pos.y) / scale) * ns }, ns));
    setZoom(z);
  };
  const onKey = (e) => {
    const d = { ArrowLeft: [8, 0], ArrowRight: [-8, 0], ArrowUp: [0, 8], ArrowDown: [0, -8] }[e.key];
    if (d) { e.preventDefault(); setPos(clamp({ x: pos.x + d[0], y: pos.y + d[1] })); }
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid="photo-cropper">
      <div
        role="img" aria-label="Drag to reposition your photo. Arrow keys also move it." tabIndex={0} onKeyDown={onKey}
        className="relative overflow-hidden rounded-full touch-none cursor-grab"
        style={{ width: VIEW, height: VIEW, border: "1.5px solid #B6A46D", background: "#fff" }}
        onPointerDown={e => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { sx: e.clientX, sy: e.clientY, p: pos }; }}
        onPointerMove={e => { if (drag.current) setPos(clamp({ x: drag.current.p.x + e.clientX - drag.current.sx, y: drag.current.p.y + e.clientY - drag.current.sy })); }}
        onPointerUp={() => { drag.current = null; }}
        data-testid="photo-cropper-viewport"
      >
        <img src={img.url} alt="" draggable={false} className="absolute max-w-none select-none pointer-events-none"
          style={{ left: pos.x, top: pos.y, width: img.w * scale, height: img.h * scale }} />
      </div>
      <div className="w-[240px]">
        <label className="text-xs tracking-widest uppercase block mb-2" style={{ color: "#0f1d2f", opacity: 0.6 }}>Zoom</label>
        <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={([z]) => setZoomKeepCenter(z)} aria-label="Zoom" data-testid="photo-cropper-zoom" />
      </div>
    </div>
  );
}
